import { X } from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

interface ModalProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	children: React.ReactNode;
	className?: string;
	closeOnOutsideClick?: boolean;
}

export function Modal({
	isOpen,
	onClose,
	title,
	children,
	className = "",
	closeOnOutsideClick = true,
}: ModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);

	// Verhindert das Scrollen des Hintergrunds, wenn das Modal geöffnet ist
	useEffect(() => {
		if (isOpen) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}
		return () => {
			document.body.classList.remove("overflow-hidden");
		};
	}, [isOpen]);

	// Schließt das Modal beim Klick außerhalb
	const handleOutsideClick = (e: React.MouseEvent) => {
		if (
			closeOnOutsideClick &&
			modalRef.current &&
			!modalRef.current.contains(e.target as Node)
		) {
			onClose();
		}
	};

	// ESC-Taste zum Schließen
	useEffect(() => {
		const handleEsc = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		window.addEventListener("keydown", handleEsc);
		return () => {
			window.removeEventListener("keydown", handleEsc);
		};
	}, [onClose]);

	if (!isOpen) return null;

	// Bestimme die Modal-Breite basierend auf dem Titel
	const isInstructionModal = title?.includes("Anleitung");
	const modalWidth = isInstructionModal ? "max-w-3xl" : "max-w-md";

	return (
		<div
			aria-modal="true"
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm transition-opacity"
			onClick={handleOutsideClick}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					onClose();
				}
			}}
			role="dialog"
			tabIndex={-1}
		>
			<div
				className={cn(
					"max-h-[90vh] w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800",
					modalWidth,
					className,
				)}
				ref={modalRef}
			>
				<div className="flex items-center justify-between border-gray-100 border-b p-4 dark:border-gray-700">
					<h3 className="font-medium text-gray-800 text-lg dark:text-gray-100">
						{title}
					</h3>
					<Button
						className="h-8 w-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
						onClick={onClose}
						size="icon"
						type="button"
						variant="ghost"
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
				<div className="p-4">{children}</div>
			</div>
		</div>
	);
}
